import { type AutovioContext, persistentAutovioContext } from "../persistent_state";
import { useRecoilState } from "recoil";
import { useLocation } from "react-router-dom";
import { useMemo } from "react";
import { restrictAccessToDrivingSchoolIds } from "../backoffice.access_control";
import { errorReportProps } from "../sentry";
import { drivingSchoolsProvider } from "../providers/drivingSchoolsProvider";

export function useAutovioContext(): [AutovioContext, (newContext: AutovioContext) => void] {
  const result = _useAutovioContext();
  const { drivingSchoolId } = result[0];
  if (drivingSchoolId) {
    errorReportProps.drivingSchoolId = drivingSchoolId;
    const drivingSchool = drivingSchoolsProvider.getOneFromCache(drivingSchoolId);
    if (drivingSchool) {
      errorReportProps.drivingSchool = drivingSchool.name;
    }
  } else {
    delete errorReportProps.drivingSchoolId;
    delete errorReportProps.drivingSchool;
  }
  return result;
}

function _useAutovioContext(): [AutovioContext, (newContext: AutovioContext) => void] {
  const { pathname: path } = useLocation();
  const [autovioContext, setAutovioContext] = useRecoilState(persistentAutovioContext);
  const result = useMemo<ReturnType<typeof useAutovioContext>>(() => {
    // Make sure, if access is restricted to one driving school, that this driving school is present in the context ...
    if (restrictAccessToDrivingSchoolIds?.length === 1) {
      return [{ drivingSchoolId: restrictAccessToDrivingSchoolIds[0] }, () => {}];
    }
    const match = /^\/drivingSchools\/([^/]+)/.exec(path);
    if (match) {
      const drivingSchoolId = match[1];
      if (autovioContext.drivingSchoolId !== drivingSchoolId) {
        setTimeout(() => setAutovioContext({ drivingSchoolId }));
      }
      return [{ drivingSchoolId }, setAutovioContext];
    }
    // These pages are not driving school specific ...
    if (
      path.startsWith("/dunning") ||
      path.startsWith("/invoiceCustomers") ||
      path.startsWith("/graduatedInvoiceCustomers") ||
      path.startsWith("/drivingLicenseAuthorities") ||
      path.startsWith("/areas") ||
      path.startsWith("/products") ||
      path.startsWith("/trainings") ||
      path.startsWith("/bundles") ||
      path.startsWith("/promoCodes") ||
      path.startsWith("/appliedReferralCodes") ||
      path.startsWith("/adas") ||
      path.startsWith("/autovioEmployees") ||
      path.startsWith("/usersToBeDeleted") ||
      path.startsWith("/bi") ||
      path.startsWith("/customization-flags-usage") ||
      path.startsWith("/examAuthorityFees")
    ) {
      return [{}, setAutovioContext];
    }
    return [autovioContext, setAutovioContext];
  }, [path, autovioContext, setAutovioContext]);
  return result;
}
